// Global Calendar Colors Configuration
// Syncs colors between Dashboard, Events page, and all calendar components

import { devError } from '../utils/debugLogger';

// Google Calendar's official 24 calendar colors
// These are the ONLY colors Google Calendar accepts - any other hex gets mapped to the closest one
// Source: https://developers.google.com/calendar/api/v3/reference/colors/get
export const GOOGLE_CALENDAR_COLOR_PALETTE = {
  1: { background: '#ac725e', name: 'Cocoa' },
  2: { background: '#d06b64', name: 'Flamingo' },
  3: { background: '#f83a22', name: 'Tomato' },
  4: { background: '#fa573c', name: 'Tangerine' },
  5: { background: '#ff7537', name: 'Pumpkin' },
  6: { background: '#ffad46', name: 'Mango' },
  7: { background: '#42d692', name: 'Eucalyptus' },
  8: { background: '#16a765', name: 'Basil' },
  9: { background: '#7bd148', name: 'Pistachio' },
  10: { background: '#b3dc6c', name: 'Avocado' },
  11: { background: '#fbe983', name: 'Citron' },
  12: { background: '#fad165', name: 'Banana' },
  13: { background: '#92e1c0', name: 'Sage' },
  14: { background: '#9fe1e7', name: 'Peacock' },
  15: { background: '#9fc6e7', name: 'Cobalt' },
  16: { background: '#4986e7', name: 'Blueberry' },
  17: { background: '#9a9cff', name: 'Lavender' },
  18: { background: '#b99aff', name: 'Wisteria' },
  19: { background: '#c2c2c2', name: 'Graphite' },
  20: { background: '#cabdbf', name: 'Birch' },
  21: { background: '#cca6ac', name: 'Radicchio' },
  22: { background: '#f691b2', name: 'Cherry Blossom' },
  23: { background: '#cd74e6', name: 'Grape' },
  24: { background: '#a47ae2', name: 'Amethyst' },
};

// Default color palette for local calendars - using Google's exact colors for perfect sync
// Ordered by visual appeal and variety
export const CALENDAR_COLORS = [
  // Greens & Teals
  '#42d692', // Eucalyptus (7)
  '#16a765', // Basil (8)
  '#7bd148', // Pistachio (9)
  '#b3dc6c', // Avocado (10)
  '#92e1c0', // Sage (13)
  
  // Blues & Purples
  '#9fe1e7', // Peacock (14)
  '#9fc6e7', // Cobalt (15)
  '#4986e7', // Blueberry (16)
  '#9a9cff', // Lavender (17)
  '#b99aff', // Wisteria (18)
  '#a47ae2', // Amethyst (24)
  '#cd74e6', // Grape (23)
  
  // Pinks & Reds
  '#f691b2', // Cherry Blossom (22)
  '#cca6ac', // Radicchio (21)
  '#d06b64', // Flamingo (2)
  '#f83a22', // Tomato (3)
  '#fa573c', // Tangerine (4)
  
  // Oranges & Yellows
  '#ff7537', // Pumpkin (5)
  '#ffad46', // Mango (6)
  '#fad165', // Banana (12)
  '#fbe983', // Citron (11)
  
  // Neutrals & Browns
  '#ac725e', // Cocoa (1)
  '#cabdbf', // Birch (20)
  '#c2c2c2', // Graphite (19)
];

// Category colors for habits, tasks, goals, events (using Google-compatible colors)
export const CATEGORY_COLORS = {
  habits: '#42d692',    // Eucalyptus - vibrant green
  tasks: '#9fc6e7',     // Cobalt - soft blue
  goals: '#b99aff',     // Wisteria - lavender purple
  events: '#ffad46',    // Mango - warm orange
};

// Default task and goal colors for calendar display
export const DEFAULT_TASK_COLOR = '#9fc6e7';  // Cobalt (matches CATEGORY_COLORS.tasks)
export const DEFAULT_GOAL_COLOR = '#b99aff';  // Wisteria (matches CATEGORY_COLORS.goals)
export const DEFAULT_EVENT_COLOR = '#ffad46'; // Mango (matches CATEGORY_COLORS.events)

// Storage keys
const GOOGLE_CALENDAR_COLORS_KEY = 'googleCalendarColors';
const GOOGLE_CALENDAR_CUSTOM_COLORS_KEY = 'googleCalendarCustomColors';
const GOOGLE_CALENDAR_VISIBILITY_KEY = 'googleCalendarVisibility';
const TASK_SETTINGS_KEY = 'taskCalendarSettings';
const GOAL_SETTINGS_KEY = 'goalCalendarSettings';
const LOCAL_CALENDARS_KEY = 'localCalendars';

/**
 * Save Google Calendar colors to localStorage for global access
 * @param {Array} calendars - Array of calendar objects with id and color
 */
export const saveGoogleCalendarColors = (calendars) => {
  try {
    const colorMap = {};
    calendars.forEach(cal => {
      colorMap[cal.id] = cal.color || cal.backgroundColor || '#4285f4';
    });
    localStorage.setItem(GOOGLE_CALENDAR_COLORS_KEY, JSON.stringify(colorMap));
  } catch (e) {
    devError('Error saving Google calendar colors:', e);
  }
};

/**
 * Get saved Google Calendar colors
 * @returns {Object} Map of calendarId -> color
 */
export const getGoogleCalendarColors = () => {
  try {
    const saved = localStorage.getItem(GOOGLE_CALENDAR_COLORS_KEY);
    return saved ? JSON.parse(saved) : {};
  } catch (e) {
    devError('Error loading Google calendar colors:', e);
    return {};
  }
};

/**
 * Get custom Google Calendar color overrides
 * @returns {Object} Map of calendarId -> custom color
 */
export const getGoogleCalendarCustomColors = () => {
  try {
    const saved = localStorage.getItem(GOOGLE_CALENDAR_CUSTOM_COLORS_KEY);
    return saved ? JSON.parse(saved) : {};
  } catch (e) {
    devError('Error loading custom Google calendar colors:', e);
    return {};
  }
};

/**
 * Set a custom color for a Google calendar
 * @param {string} calendarId - Google calendar ID
 * @param {string} color - Color hex value
 */
export const setGoogleCalendarCustomColor = (calendarId, color) => {
  try {
    const customColors = getGoogleCalendarCustomColors();
    customColors[calendarId] = color;
    localStorage.setItem(GOOGLE_CALENDAR_CUSTOM_COLORS_KEY, JSON.stringify(customColors));
  } catch (e) {
    devError('Error saving custom Google calendar color:', e);
  }
};

/**
 * Get Google calendar visibility settings from localStorage
 * @returns {Object} Map of calendarId -> visible (boolean)
 */
export const getGoogleCalendarVisibility = () => {
  try {
    const saved = localStorage.getItem(GOOGLE_CALENDAR_VISIBILITY_KEY);
    if (saved) return JSON.parse(saved);
  } catch (e) {
    devError('Error loading Google calendar visibility:', e);
  }
  return {};
};

/**
 * Set visibility for a Google calendar
 * @param {string} calendarId - Google calendar ID
 * @param {boolean} visible - Whether calendar is visible
 */
export const setGoogleCalendarVisibility = (calendarId, visible) => {
  try {
    const visibility = getGoogleCalendarVisibility();
    visibility[calendarId] = visible;
    localStorage.setItem(GOOGLE_CALENDAR_VISIBILITY_KEY, JSON.stringify(visibility));
  } catch (e) {
    devError('Error saving Google calendar visibility:', e);
  }
};

/**
 * Get color for a specific Google calendar by ID (respects custom overrides)
 * @param {string} calendarId - Google calendar ID
 * @returns {string} Color hex value
 */
export const getGoogleCalendarColor = (calendarId) => {
  // First check for custom override
  const customColors = getGoogleCalendarCustomColors();
  if (customColors[calendarId]) {
    return customColors[calendarId];
  }
  // Fall back to API color
  const colors = getGoogleCalendarColors();
  return colors[calendarId] || '#4285f4'; // Default Google blue
};

/**
 * Get task calendar settings (color, visibility, and sync)
 * @returns {Object} { visible: boolean, color: string, syncToGoogle: boolean, googleCalendarId: string|null }
 */
export const getTaskSettings = () => {
  try {
    const saved = localStorage.getItem(TASK_SETTINGS_KEY);
    if (saved) return JSON.parse(saved);
  } catch (e) {
    devError('Error loading task settings:', e);
  }
  return { visible: true, color: DEFAULT_TASK_COLOR, syncToGoogle: false, googleCalendarId: null };
};

/**
 * Get goal calendar settings (color, visibility, and sync)
 * @returns {Object} { visible: boolean, color: string, syncToGoogle: boolean, googleCalendarId: string|null }
 */
export const getGoalSettings = () => {
  try {
    const saved = localStorage.getItem(GOAL_SETTINGS_KEY);
    if (saved) return JSON.parse(saved);
  } catch (e) {
    devError('Error loading goal settings:', e);
  }
  return { visible: true, color: DEFAULT_GOAL_COLOR, syncToGoogle: false, googleCalendarId: null };
};

/**
 * Get local calendars
 * @deprecated Use calendars from AppContext instead. This function is only for backward compatibility
 * during migration and for non-React code that needs calendar info.
 * @returns {Array} Array of local calendar objects
 */
export const getLocalCalendars = () => {
  // Note: Calendars are now stored in Firestore via AppContext
  // This function only returns a default fallback
  // The actual calendars should be accessed via useApp().calendars
  return [{ id: 'default', name: 'My Calendar', color: CALENDAR_COLORS[0], visible: true, type: 'local' }];
};

/**
 * Get color for an event based on its source
 * @param {Object} event - Event object
 * @param {Object} options - Optional overrides { taskColor, goalColor, localCalendars }
 * @returns {string} Color hex value
 */
export const getEventColor = (event, options = {}) => {
  // Task item
  if (event.itemType === 'task') {
    return options.taskColor || getTaskSettings().color || DEFAULT_TASK_COLOR;
  }
  
  // Goal item
  if (event.itemType === 'goal') {
    return options.goalColor || getGoalSettings().color || DEFAULT_GOAL_COLOR;
  }
  
  // Check if this is a LOCAL calendar event first (even if synced to Google)
  // Local calendar events have calendarId like 'default', 'calendar_123', etc.
  if (event.calendarId && !event.isGoogleEvent) {
    const isLocalCalendarId = !event.calendarId.includes('@') && event.calendarId.length < 40;
    if (isLocalCalendarId) {
      // Use passed-in localCalendars if available, otherwise fallback
      const localCalendars = options.localCalendars || getLocalCalendars();
      const calendar = localCalendars.find(c => c.id === event.calendarId);
      if (calendar && calendar.color) return calendar.color;
    }
  }
  
  // For pure Google events (from google_events collection), check custom calendar color override
  // This allows users to customize colors for imported Google calendars
  if (event.isGoogleEvent) {
    // First check calendarId (used by google_events collection - this IS the Google calendar ID)
    if (event.calendarId) {
      const customColor = getGoogleCalendarColor(event.calendarId);
      if (customColor) return customColor;
    }
    // Then check googleCalendarId as fallback
    if (event.googleCalendarId) {
      const customColor = getGoogleCalendarColor(event.googleCalendarId);
      if (customColor) return customColor;
    }
  }
  
  // If event has explicit color, use it
  if (event.color) return event.color;
  
  // If event has calendarColor from Google sync (fallback if no custom override)
  if (event.calendarColor) return event.calendarColor;
  
  // Default event color
  return DEFAULT_EVENT_COLOR;
};

/**
 * Get all calendar colors for a list of calendars (for sync)
 * @returns {Object} Combined color map for all calendar types
 */
export const getAllCalendarColors = () => {
  const googleColors = getGoogleCalendarColors();
  const localCalendars = getLocalCalendars();
  const taskSettings = getTaskSettings();
  const goalSettings = getGoalSettings();
  
  const localColors = {};
  localCalendars.forEach(cal => {
    localColors[cal.id] = cal.color;
  });
  
  return {
    google: googleColors,
    local: localColors,
    tasks: taskSettings.color,
    goals: goalSettings.color,
    categories: CATEGORY_COLORS,
  };
};
